/**
 * The kiosk's merchant-settings reads, in one file — the same split as
 * `kiosk/catalog/selectors.ts` and `channel/selectors.ts`.
 *
 * §10's rule: a domain with a UI surface has TWO adapters — the imperative `./index` for
 * callbacks and `./hooks` for render — and two adapters naming store keys independently is
 * exactly the drift the original "index.ts is the only file that reads state" rule existed
 * to prevent. This file is where that rule moved to; both halves consume it.
 *
 * It appeared when banners got a reactive consumer (§10: "add `selectors.ts` and `hooks/`
 * when a screen needs to re-render on the state, not before" — two screens now do).
 *
 * ⚠️ These take STATE; they never reach for the store themselves. `./index` keeps its
 * `store?.getState?.()` guard, because that module sits inside a real import cycle with the
 * store — read its header before copying the plain form anywhere near `apiService`.
 */
import type {
  KioskBanner,
  KioskConfig,
  KioskContactPolicy,
  KioskServiceConfig,
} from "./types";

/** Is this device in kiosk mode? Persisted, so it survives a restart. */
export function selectKioskEnabled(state: any): boolean {
  return Boolean(state?.kioskConfigStore?.enabled);
}

/** null until the config API has answered at least once on this device. */
export function selectKioskConfig(state: any): KioskConfig | null {
  return state?.kioskConfigStore?.config ?? null;
}

/** Merchant brand colour, or undefined before the config lands. The theme falls back. */
export function selectPrimaryColor(state: any): string | undefined {
  return selectKioskConfig(state)?.primaryColor;
}

/**
 * The merchant's trading name — the kiosk header's title and the footer's legal line.
 *
 * ⚠️ Reads `businessStore`, not `kioskConfigStore`, and that is on purpose: the name is the
 * merchant's own rather than a kiosk setting, so it is already in the POS store AND persisted
 * — the header survives a boot with no network, which the kiosk config (no fetch yet, §13.7
 * step 4) does not. Same precedent as `kiosk/service/selectors.ts` reading `catalogStore`: a
 * kiosk selector may read a slice no OTHER kiosk adapter owns.
 *
 * TODO: no logo field exists on `businessData` or on `KioskConfig`; add `selectMerchantLogoUrl`
 * beside this when one does — `KioskBrand.logoUrl` is already threaded through the theme.
 */
export function selectMerchantName(state: any): string | undefined {
  return state?.businessStore?.businessData?.businessName;
}

/**
 * The shared empties for the unconfigured case — one per selector that can answer "nothing".
 *
 * 🔴 **`?? []` inline is a bug, not a style choice.** `useSelector` compares by IDENTITY, so a
 * fallback literal is a brand-new array on every call and makes EVERY store notification look
 * like a change. It shows up two ways, and the mild one is not mild:
 *
 *  - A consumer that derives state in an effect LOOPS. The kiosk payment hook rebuilt its
 *    record, the engine re-seeded, the re-seed dispatched, and the dispatch re-ran the
 *    selector — React caps that at "Maximum update depth exceeded", a crash. That is why
 *    `NO_MODE_IDS` was added, and for a while it was the only one of these that existed.
 *  - Every other consumer just re-renders forever, silently. `selectBanners` had it, and
 *    because the kiosk's banners are `[]` until the config API answers — which on a LAN with
 *    no internet is the whole session — `useKioskBanners` handed the catalog screen a new
 *    array on every dispatch in the app, so the banner carousel repainted on every tap of
 *    every card. Measured, and pinned by `catalogRenders.test.tsx`; the identical hazard on
 *    `ordering/cart/selectors.ts` has the longer account.
 *
 * ⚠️ **They are SHARED, so nothing may mutate what these return on a miss.** Every consumer
 * today reads them (`map`/`filter`/`sort` into a copy); a writer must copy first.
 * `sortByDisplayOrder` does, which is what keeps `useKioskBanners` honest.
 */
const NO_SERVICES: KioskServiceConfig[] = [];
const NO_BANNERS: KioskBanner[] = [];
const NO_MODE_IDS: string[] = [];
const NO_SEARCHES: string[] = [];

/**
 * Configured services, RAW — unresolved and unsorted.
 *
 * `kiosk/service` is what turns these into a renderable list: it sorts them and drops any
 * whose channel this branch cannot serve. Never render this array directly.
 */
export function selectConfiguredServices(state: any): KioskServiceConfig[] {
  return selectKioskConfig(state)?.services ?? NO_SERVICES;
}

/** Merchant banner content, RAW — sorting is `sortByDisplayOrder`'s job, in one place. */
export function selectBanners(state: any): KioskBanner[] {
  return selectKioskConfig(state)?.banners ?? NO_BANNERS;
}

/** Payment modes the merchant allows AT THE KIOSK — a subset of the POS's. */
export function selectKioskPaymentModeIds(state: any): string[] {
  return selectKioskConfig(state)?.paymentModeIds ?? NO_MODE_IDS;
}

/**
 * Merchant-promoted search terms, RAW. `[]` when unconfigured, which is the common case —
 * the search screen draws no chip row at all rather than a heading over nothing.
 */
export function selectPopularSearches(state: any): string[] {
  return selectKioskConfig(state)?.popularSearches ?? NO_SEARCHES;
}

/**
 * The merchant's contact-collection setting, RAW — `undefined` when unconfigured.
 *
 * Do not compare this to a string. `contactPolicy()` in `kiosk/customer` is what turns it into
 * one of the three known values, and it is the only place the "absent means Disabled" default
 * is written down.
 */
export function selectContactPolicySetting(state: any): KioskContactPolicy | undefined {
  return selectKioskConfig(state)?.customerPhoneNumber;
}
